import { useState, useEffect, type FormEvent, useCallback } from 'react';
import { useUserLocation, useLogin } from '@/features/auth/hooks/useLogin';
import { isValidEmail } from '@chikara/utils';
import { Button, FormField } from '@chikara/ui';
import { Link } from '@chikara/ui';
import { Icon } from '@chikara/ui';
import { Modal } from '@chikara/ui';
import { t } from '@/i18n';
import styles from './LoginForm.module.css';
import { useSpinner } from '@chikara/ui';


export function LoginForm({}) {
  const { loginWithEmail } = useLogin();
  const { isSpinnerLoading } = useSpinner();
  const { 
    locationAllowed, 
    showLocationModal, 
    locationError, 
    errorCode,
    closeRequest, 
    openRequest, 
    requestLocationPermission 
  } = useUserLocation();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isPendingSubmit, setIsPendingSubmit] = useState(false);
  const [validationErrors, setValidationErrors] = useState<{
    email?: string;
    password?: string;
  }>({});
  const codeLength: number = 8;

  const getLocationErrorMessage = () => {
    if (errorCode === 1) return t('location.permissionDenied');
    if (errorCode === 2 || errorCode === 3) return t('location.positionUnavailable');
    return t('location.geoError');
  };

  const isFormValid = isValidEmail(email) && password.length >= codeLength &&  !isSpinnerLoading;

  const validate = useCallback((): boolean => {
    const errors: typeof validationErrors = {};

    if (!email) {
      errors.email = t('login.emailRequired');
    } else if (!isValidEmail(email)) {
      errors.email = t('login.emailValid');
    }

    if (!password) {
      errors.password = t('login.passRequired');
    } else if (password.length < codeLength) {
      errors.password = t('login.passValid');
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  }, [email, password, codeLength]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    
    if (!locationAllowed) {
      setIsPendingSubmit(true);
      openRequest();
      return;
    }

    await submitLogin();
  };


  const submitLogin = useCallback(async () => {
    if (!validate()) return;
    await loginWithEmail(email, password);
  }, [email, password, validate, loginWithEmail]);

  useEffect(() => {
    if (locationAllowed && isPendingSubmit && isValidEmail(email) && password.length >= codeLength) {
      setIsPendingSubmit(false);
      submitLogin();
    }
  }, [locationAllowed, isPendingSubmit, submitLogin, email, password, codeLength]);

  return (
    <form className={styles.form} onSubmit={handleSubmit}>
      <div className={styles.header}>
        <h2 className={styles.title}>{t('login.title')}</h2>
        <p className={styles.subTitle}>{t('login.subtitle')}</p>
      </div>

      <FormField
        label={t('login.email')}
        type="email"
        value={email}
        onChange={e => setEmail(e.target.value)}
        error={validationErrors.email}
        required
      />

      <FormField
        label={t('login.password')}
        type="password"
        value={password}
        onChange={e => setPassword(e.target.value)}
        error={validationErrors.password}
        required
      />

      <Link href="/forgot-password" className={styles.forgotPassword} variant="primary" size="sm">
        {t('login.forgotPassword')}
      </Link>
      <Button type="submit" disabled={!isFormValid} isLoading={isSpinnerLoading} className={styles.submitButton}>
        {t('login.submit')}
      </Button>

      <Modal 
        style={{visibility: isSpinnerLoading ? "hidden" : "visible"}}
        isOpen={showLocationModal}
        title={t('location.modalTitle')}
        description={t('location.modalDescription')}
        icon={<img src="/logo_delta.svg" alt="delt.ai" width={56} height={56} />}
        actionLabel={t('location.action')}
        closeOnOverlayClick={true}
        closeOnEsc={true}
        showCloseButton={true}
        onClose={() => closeRequest()}
        onAction={requestLocationPermission}
        alert={locationError ? {
          variant: "error", 
          icon: <Icon name="error" color="inherit" />, 
          className: styles.error, 
          children: getLocationErrorMessage() 
        } : undefined}
      />
    </form>
  );
}
